/**
 * A link's window and whether it holds at NOW, as text and a badge (1.J stage 3, deliverables 28
 * and 31). Shown wherever 1.J lists a link with `chrono` on; read from `LinkV1` and NOW only.
 */
import type { CompiledCalendar } from '@codex/calendar';
import type { LinkV1 } from '@codex/contracts';
import { Icon, StateBadge, type StateSpec } from '@codex/ui';
import { INVALID_SPAN } from '../../when/words.js';
import { hasValidity, stateShown, windowText } from './validity-model.js';

export interface WhenFacts {
  readonly calendar: CompiledCalendar | null;
  readonly now: number | null;
}

const NEUTRAL: StateSpec = { icon: 'clock', label: '', tone: 'neutral' };
const WARNING: StateSpec = { icon: 'warning', label: '', tone: 'warning' };

/** The window and state words of `link`, or null for a link without validity. */
export function validityWords(
  link: LinkV1,
  docId: string,
  facts: WhenFacts,
): { window: string | null; state: string | null } | null {
  if (!hasValidity(link) || facts.calendar === null) return null;
  const window = windowText(link, docId, new Map(), facts.calendar);
  return { window, state: stateShown(link, facts.now, window) };
}

export function ValidityCells({
  link,
  docId,
  facts,
}: {
  link: LinkV1;
  docId: string;
  facts: WhenFacts;
}) {
  const words = validityWords(link, docId, facts);
  if (words === null || (words.window === null && words.state === null)) return null;
  const invalid = words.window === INVALID_SPAN;
  return (
    <span className="cx-links-when" data-validity="">
      {words.window &&
        (invalid ? (
          <StateBadge state={WARNING} label={words.window} />
        ) : (
          <span className="cx-links-when__window">
            <Icon name="calendar" size={14} />
            {words.window}
          </span>
        ))}
      {words.state && (
        <StateBadge state={words.state === INVALID_SPAN ? WARNING : NEUTRAL} label={words.state} />
      )}
    </span>
  );
}
